if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.onPaymentButtonClickBound = this.onPaymentButtonClick.bind(this);
        this.onRequiredVariantChangeBound = this.onRequiredVariantChange.bind(this);
        this.syncRequiredVariantStateBound = this.syncRequiredVariantState.bind(this);
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.addEventListener('click', this.onPaymentButtonClickBound, true);
        document.addEventListener('change', this.onRequiredVariantChangeBound, true);
        this.paymentButtonObserver = new MutationObserver(this.syncRequiredVariantStateBound);
        this.paymentButtonObserver.observe(this, { childList: true, subtree: true });
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.debugRequiredVariant('init', {
          sectionId: this.dataset.sectionId,
          hasPaymentButton: Boolean(this.querySelector('.shopify-payment-button')),
          requiredSelects: this.getRequiredVariantSelects().map((select) => ({
            id: select.id,
            value: select.value,
            label: select.closest('.product-form__input')?.querySelector('.form__label')?.textContent.trim(),
          })),
        });
        this.syncRequiredVariantState();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onRequiredVariantChangeBound, true);
        this.paymentButtonObserver?.disconnect();
      }

      onSubmitHandler(evt) {
        this.debugRequiredVariant('submit', {
          submitter: evt.submitter?.outerHTML,
          variantId: this.variantIdInput.value,
        });
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (!this.validateRequiredVariantSelections()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      onPaymentButtonClick(evt) {
        const checkoutButton = evt.target.closest(
          '.shopify-payment-button, .sr-headless-checkout, [class*="sr-headless-checkout"]'
        );
        this.debugRequiredVariant('capture click', {
          target: evt.target.tagName,
          targetClass: evt.target.className,
          insideCheckoutButton: Boolean(checkoutButton),
          checkoutButtonClass: checkoutButton?.className,
          hasIncompleteSelection: this.hasIncompleteRequiredVariantSelections(),
        });
        if (!checkoutButton) return;
        if (this.validateRequiredVariantSelections()) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
        evt.stopPropagation();
        this.debugRequiredVariant('blocked checkout button click', {
          checkoutButtonClass: checkoutButton.className,
        });
      }

      onRequiredVariantChange(evt) {
        const isRequiredSelect = this.getRequiredVariantSelects().includes(evt.target);
        this.debugRequiredVariant('document change', {
          targetId: evt.target.id,
          targetValue: evt.target.value,
          isRequiredSelect,
        });
        if (!isRequiredSelect) return;
        this.syncRequiredVariantState();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.hasIncompleteRequiredVariantSelections()) {
          disable = false;
          text = window.variantStrings.addToCart;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      syncRequiredVariantState() {
        const hasIncompleteSelection = this.hasIncompleteRequiredVariantSelections();
        this.debugRequiredVariant('sync state', {
          hasIncompleteSelection,
          variantIdBeforeSync: this.variantIdInput.value,
          requiredSelects: this.getRequiredVariantSelects().map((select) => ({
            id: select.id,
            value: select.value,
          })),
        });

        if (hasIncompleteSelection) {
          this.variantIdInput.value = '';
          this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.syncPaymentButtonGuard(hasIncompleteSelection);
      }

      syncPaymentButtonGuard(hasIncompleteSelection = this.hasIncompleteRequiredVariantSelections()) {
        const paymentButton = this.querySelector('.shopify-payment-button');
        this.debugRequiredVariant('sync payment guard', {
          hasPaymentButton: Boolean(paymentButton),
          hasIncompleteSelection,
        });
        if (!paymentButton) return;

        let guardButton = paymentButton.querySelector('.shopify-payment-button-required-guard');

        if (!guardButton) {
          this.debugRequiredVariant('create payment guard', {
            paymentButtonHtml: paymentButton.outerHTML,
          });
          guardButton = document.createElement('button');
          guardButton.type = 'button';
          guardButton.className = 'shopify-payment-button-required-guard';
          guardButton.textContent =
            paymentButton.querySelector('.shopify-payment-button__button')?.textContent.trim() || 'Buy it now';
          guardButton.addEventListener('click', (event) => {
            this.debugRequiredVariant('guard click', {
              hasIncompleteSelection: this.hasIncompleteRequiredVariantSelections(),
            });
            event.preventDefault();
            event.stopPropagation();
            this.validateRequiredVariantSelections();
          });
          paymentButton.appendChild(guardButton);
        }

        paymentButton.classList.toggle('shopify-payment-button--requires-selection', hasIncompleteSelection);
        guardButton.hidden = !hasIncompleteSelection;
        guardButton.toggleAttribute('aria-hidden', !hasIncompleteSelection);
        this.debugRequiredVariant('payment guard state', {
          guardHidden: guardButton.hidden,
          guardAriaHidden: guardButton.getAttribute('aria-hidden'),
          paymentButtonClass: paymentButton.className,
        });
      }

      validateRequiredVariantSelections() {
        const requiredSelects = this.getRequiredVariantSelects();
        const firstInvalidSelect = requiredSelects.find((select) => !select.value);

        this.debugRequiredVariant('validate', {
          requiredSelects: requiredSelects.map((select) => ({
            id: select.id,
            value: select.value,
            selectedText: select.selectedOptions[0]?.textContent.trim(),
          })),
          firstInvalidSelect: firstInvalidSelect?.id,
        });

        requiredSelects.forEach((select) => {
          select.closest('.product-form__input')?.classList.toggle('product-form__input--error', !select.value);
          select.toggleAttribute('aria-invalid', !select.value);
        });

        if (!firstInvalidSelect) return true;

        this.variantIdInput.value = '';
        firstInvalidSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (!this.isIOS()) {
          firstInvalidSelect.focus({ preventScroll: true });
        }

        return false;
      }

      hasIncompleteRequiredVariantSelections() {
        return this.getRequiredVariantSelects().some((select) => !select.value);
      }

      getRequiredVariantSelects() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return [];

        return Array.from(
          document.querySelectorAll(
            `variant-selects[data-section="${sectionId}"] select[data-requires-selection="true"]`
          )
        );
      }

      isIOS() {
        return (
          /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        );
      }

      debugRequiredVariant(message, data = undefined) {
        console.log(`[Required Variant][${this.dataset.sectionId || 'unknown'}] ${message}`, data ?? '');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
